import React from 'react';

/**
 * ============================================================================
 * HEAREN — CLINICAL AUDIOGRAM CHART
 * ============================================================================
 *
 * Responsabilité :
 * reconstruire visuellement un audiogramme clinique à partir des seuils
 * structurés renvoyés par le backend.
 *
 * Convention :
 * - oreille droite : cercle rouge O ;
 * - oreille gauche : croix bleue X ;
 * - fréquences : 125 Hz à 8 000 Hz ;
 * - intensité : -10 à 120 dB HL.
 * ============================================================================
 */

const FREQUENCIES = [
  125,
  250,
  500,
  750,
  1000,
  1500,
  2000,
  3000,
  4000,
  6000,
  8000,
];

const MAJOR_FREQUENCIES = new Set([
  125,
  250,
  500,
  1000,
  2000,
  4000,
  8000,
]);

const DB_LEVELS = [
  -10,
  0,
  10,
  20,
  30,
  40,
  50,
  60,
  70,
  80,
  90,
  100,
  110,
  120,
];

const WIDTH = 820;
const HEIGHT = 560;

const MARGIN = {
  top: 55,
  right: 55,
  bottom: 80,
  left: 80,
};

const GRAPH_WIDTH =
  WIDTH - MARGIN.left - MARGIN.right;

const GRAPH_HEIGHT =
  HEIGHT - MARGIN.top - MARGIN.bottom;

/**
 * Convertit différentes formes possibles en objet :
 *
 * {
 *   125: 15,
 *   250: 20,
 *   500: 25
 * }
 */
const normalizeThresholds = (value) => {
  if (!value) {
    return {};
  }

  /*
   * Forme déjà normalisée :
   * { 125: 15, 250: 20 }
   */
  if (
    typeof value === 'object' &&
    !Array.isArray(value)
  ) {
    return Object.entries(value).reduce(
      (result, [frequency, threshold]) => {
        if (
          threshold === null ||
          threshold === undefined ||
          threshold === ''
        ) {
          return result;
        }

        const parsedFrequency = Number(frequency);
        const parsedThreshold = Number(threshold);

        if (
          Number.isFinite(parsedFrequency) &&
          Number.isFinite(parsedThreshold)
        ) {
          result[parsedFrequency] = parsedThreshold;
        }

        return result;
      },
      {}
    );
  }

  /*
   * Forme tableau éventuelle :
   * [
   *   { frequency: 125, threshold: 15 },
   *   { frequency_hz: 250, db_hl: 20 }
   * ]
   */
  if (Array.isArray(value)) {
    return value.reduce((result, item) => {
      const frequency = Number(
        item?.frequency ??
        item?.frequency_hz ??
        item?.hz
      );

      const threshold = Number(
        item?.threshold ??
        item?.threshold_db ??
        item?.db_hl ??
        item?.value
      );

      if (
        Number.isFinite(frequency) &&
        Number.isFinite(threshold)
      ) {
        result[frequency] = threshold;
      }

      return result;
    }, {});
  }

  return {};
};

const getX = (frequency) => {
  const index = FREQUENCIES.indexOf(
    Number(frequency)
  );

  if (index < 0) {
    return null;
  }

  return (
    MARGIN.left +
    (index / (FREQUENCIES.length - 1)) *
      GRAPH_WIDTH
  );
};

const getY = (db) => {
  const minimumDb = DB_LEVELS[0];
  const maximumDb =
    DB_LEVELS[DB_LEVELS.length - 1];

  const normalized =
    (Number(db) - minimumDb) /
    (maximumDb - minimumDb);

  return (
    MARGIN.top +
    normalized * GRAPH_HEIGHT
  );
};

const createPoints = (thresholds) => {
  return FREQUENCIES
    .map((frequency) => {
      const threshold =
        thresholds[frequency];

      if (
        threshold === null ||
        threshold === undefined ||
        !Number.isFinite(Number(threshold))
      ) {
        return null;
      }

      return {
        frequency,
        threshold: Number(threshold),
        x: getX(frequency),
        y: getY(threshold),
      };
    })
    .filter(Boolean);
};

const createPolylinePoints = (points) => {
  return points
    .map((point) => `${point.x},${point.y}`)
    .join(' ');
};

const RightEarSymbol = ({ x, y }) => {
  return (
    <circle
      cx={x}
      cy={y}
      r="8"
      fill="white"
      stroke="#ef4444"
      strokeWidth="4"
    />
  );
};

const LeftEarSymbol = ({ x, y }) => {
  const size = 8;

  return (
    <g
      stroke="#3b82f6"
      strokeWidth="4"
      strokeLinecap="round"
    >
      <line
        x1={x - size}
        y1={y - size}
        x2={x + size}
        y2={y + size}
      />

      <line
        x1={x + size}
        y1={y - size}
        x2={x - size}
        y2={y + size}
      />
    </g>
  );
};

const ClinicalAudiogramChart = ({
  rightEar,
  leftEar,
  title = 'Audiogramme reconstruit',
}) => {
  const rightThresholds =
    normalizeThresholds(rightEar);

  const leftThresholds =
    normalizeThresholds(leftEar);

  const rightPoints =
    createPoints(rightThresholds);

  const leftPoints =
    createPoints(leftThresholds);

  const hasRightEar =
    rightPoints.length > 0;

  const hasLeftEar =
    leftPoints.length > 0;

  const hasData =
    hasRightEar || hasLeftEar;

  if (!hasData) {
    return (
      <div
        className="
          rounded-3xl
          border border-white/15
          bg-black/10
          p-6
        "
      >
        <div className="text-lg font-medium text-white">
          {title}
        </div>

        <p className="mt-2 text-white/60">
          Aucun seuil exploitable n’est encore disponible
          pour reconstruire l’audiogramme.
        </p>
      </div>
    );
  }

  return (
    <div
      className="
        overflow-hidden
        rounded-3xl
        border border-white/15
        bg-white
        p-4
      "
    >
      <div className="px-2 pb-4">
        <h3 className="text-xl font-semibold text-slate-900">
          {title}
        </h3>

        <p className="mt-1 text-sm text-slate-500">
          Représentation reconstruite à partir des seuils
          détectés par Hearen.
        </p>
      </div>

      <div className="w-full">
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role="img"
          aria-label="Audiogramme clinique reconstruit"
          className="block w-full h-auto max-w-[520px] mx-auto"
        >
          <rect
            x="0"
            y="0"
            width={WIDTH}
            height={HEIGHT}
            fill="white"
          />

          {/* Titre de l’axe horizontal */}
          <text
            x={MARGIN.left + GRAPH_WIDTH / 2}
            y={HEIGHT - 20}
            textAnchor="middle"
            fontSize="17"
            fontWeight="600"
            fill="#334155"
          >
            Fréquence en Hz
          </text>

          {/* Titre de l’axe vertical */}
          <text
            x="22"
            y={MARGIN.top + GRAPH_HEIGHT / 2}
            textAnchor="middle"
            fontSize="17"
            fontWeight="600"
            fill="#334155"
            transform={`
              rotate(
                -90
                22
                ${MARGIN.top + GRAPH_HEIGHT / 2}
              )
            `}
          >
            Niveau auditif en dB HL
          </text>

          {/* Lignes horizontales et valeurs dB */}
          {DB_LEVELS.map((db) => {
            const y = getY(db);

            return (
              <g key={db}>
                <line
                  x1={MARGIN.left}
                  y1={y}
                  x2={MARGIN.left + GRAPH_WIDTH}
                  y2={y}
                  stroke={
                    db === 0
                      ? '#64748b'
                      : '#cbd5e1'
                  }
                  strokeWidth={
                    db === 0 ? 1.8 : 1
                  }
                />

                <text
                  x={MARGIN.left - 14}
                  y={y + 5}
                  textAnchor="end"
                  fontSize="14"
                  fill="#475569"
                >
                  {db}
                </text>
              </g>
            );
          })}

          {/* Lignes verticales et fréquences */}
          {FREQUENCIES.map((frequency) => {
            const x = getX(frequency);
            const isMajor =
              MAJOR_FREQUENCIES.has(frequency);

            return (
              <g key={frequency}>
                <line
                  x1={x}
                  y1={MARGIN.top}
                  x2={x}
                  y2={MARGIN.top + GRAPH_HEIGHT}
                  stroke={
                    isMajor
                      ? '#94a3b8'
                      : '#e2e8f0'
                  }
                  strokeWidth={
                    isMajor ? 1.2 : 1
                  }
                  strokeDasharray={
                    isMajor ? undefined : '5 5'
                  }
                />

                <text
                  x={x}
                  y={MARGIN.top + GRAPH_HEIGHT + 27}
                  textAnchor="middle"
                  fontSize={
                    isMajor ? '14' : '12'
                  }
                  fontWeight={
                    isMajor ? '600' : '400'
                  }
                  fill={
                    isMajor
                      ? '#334155'
                      : '#64748b'
                  }
                >
                  {frequency >= 1000
                    ? `${frequency / 1000}k`
                    : frequency}
                </text>
              </g>
            );
          })}

          {/* Bordure du graphique */}
          <rect
            x={MARGIN.left}
            y={MARGIN.top}
            width={GRAPH_WIDTH}
            height={GRAPH_HEIGHT}
            fill="none"
            stroke="#475569"
            strokeWidth="2"
          />

          {/* Courbe oreille droite */}
          {rightPoints.length > 1 && (
            <polyline
              points={createPolylinePoints(
                rightPoints
              )}
              fill="none"
              stroke="#ef4444"
              strokeWidth="3"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {/* Courbe oreille gauche */}
          {leftPoints.length > 1 && (
            <polyline
              points={createPolylinePoints(
                leftPoints
              )}
              fill="none"
              stroke="#3b82f6"
              strokeWidth="3"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}

          {/* Symboles oreille droite */}
          {rightPoints.map((point) => (
            <g
              key={`right-${point.frequency}`}
            >
              <RightEarSymbol
                x={point.x}
                y={point.y}
              />

              <title>
                {`Oreille droite : ${point.frequency} Hz, ${point.threshold} dB HL`}
              </title>
            </g>
          ))}

          {/* Symboles oreille gauche */}
          {leftPoints.map((point) => (
            <g
              key={`left-${point.frequency}`}
            >
              <LeftEarSymbol
                x={point.x}
                y={point.y}
              />

              <title>
                {`Oreille gauche : ${point.frequency} Hz, ${point.threshold} dB HL`}
              </title>
            </g>
          ))}

          {/* Légende */}
          <g
            transform={`
              translate(
                ${MARGIN.left + 15}
                25
              )
            `}
          >
            {hasRightEar && (
              <g>
                <RightEarSymbol
                  x={8}
                  y={0}
                />

                <text
                  x="28"
                  y="5"
                  fontSize="14"
                  fill="#334155"
                >
                  Oreille droite
                </text>
              </g>
            )}

            {hasLeftEar && (
              <g transform="translate(170 0)">
                <LeftEarSymbol
                  x={8}
                  y={0}
                />

                <text
                  x="28"
                  y="5"
                  fontSize="14"
                  fill="#334155"
                >
                  Oreille gauche
                </text>
              </g>
            )}
          </g>
        </svg>
      </div>
    </div>
  );
};

export default ClinicalAudiogramChart;